// Copyright (c) StrangeBee 2023

import { ReactElement, useMemo, useState } from 'react';
import { TaskLogOutput, TaskLogInput, TaskOutput, TaskStatus, PermissionsEnum } from 'thehive-sdk';
import { findUser, useLocalMessage } from '@Helpers/';
import { useListAllUsers } from '@Queries/userQueries';
import { ResponderDrawer } from '@Organisms/Responder';
import { SystemUser } from '@Constants/organisation';
import { TaskLogManage } from '../TaskLogManage/TaskLogManage';
import TaskLogItemReports from '../TaskLogItemReports/TaskLogItemReports';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { ICONS } from '@Constants/iconsApp';
import { onCaseImageUpload } from '@Helpers/download';
import { TaskEventList } from '@Organisms/Task/eventList';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { ListActionType } from '@Types';
import { GenericAttachmentType, AttachmentParentType } from '@Molecules/THPreview';
import { Comment } from '@Molecules/Comment/Comment';

import './TaskLogItem.scss';

interface TaskLogItemProps {
    taskLog: TaskLogOutput;
    deleteTaskLog: (taskLogId: string) => void;
    editTaskLog: (taskLog: TaskLogInput) => Promise<any>;
    enableResponder?: boolean;
    caseId: string;
    taskId: string;
    taskEventList: TaskEventList;
    task?: TaskOutput;
    disableDeletion?: boolean;
    togglePreview?: (attachment: GenericAttachmentType, parent?: AttachmentParentType) => void;
}

const TaskLogItem = ({
    taskLog,
    deleteTaskLog,
    editTaskLog,
    enableResponder = true,
    caseId,
    taskId,
    taskEventList,
    task,
    disableDeletion = false,
    togglePreview,
}: TaskLogItemProps): ReactElement => {
    const i = useLocalMessage();
    const { data: allUsersList = [] } = useListAllUsers();

    const [showReports, setShowReports] = useState(false);
    const {
        _id,
        message,
        attachments,
        includeInTimeline,
        _createdAt,
        _createdBy,
        extraData: { actionCount = 0 },
    } = taskLog || {};

    const assigneeUser = useMemo(
        () => (_createdBy !== SystemUser.login ? findUser(_createdBy, allUsersList) : SystemUser),
        [_createdBy, allUsersList],
    );

    const { name = '', avatar = '' } = assigneeUser || {};

    const onEditFormFinish = (input: TaskLogInput): Promise<any> => {
        return editTaskLog(input);
    };

    const toggleReports = (): void => {
        setShowReports((reports) => !reports);
    };

    const { extraData: { case: { userPermissions = [] } = {} } = {} } = task || {};

    const hasPermissions = useSplitPermissions(userPermissions, [
        PermissionsEnum.MANAGE_TASK,
        PermissionsEnum.MANAGE_ACTION,
    ]);

    const actions: { [key: string]: ListActionType } = {
        edit: {
            hidden: !hasPermissions.task,
            label: i('task-log.actions.edit'),
            icon: <ICONS.EditIcon />,
            CustomComponent: (trigger) => (
                <TaskLogManage
                    onTaskClose={() =>
                        taskEventList.onEdit(
                            {
                                status: TaskStatus.COMPLETED,
                            },
                            taskId,
                        )
                    }
                    task={task}
                    key={`${_id}-tasklog-manage`}
                    triggerElem={trigger}
                    onFormFinish={onEditFormFinish}
                    taskLog={taskLog}
                />
            ),
        },
        delete: {
            hidden: !hasPermissions.task,
            label: i('task-log.actions.remove'),
            icon: <ICONS.DeleteIcon />,
            confirm: true,
            message: i('task.logs.item.deleteText'),
            query: (): void => deleteTaskLog(_id),
            disabled: disableDeletion,
        },
        responders: {
            hidden: !(enableResponder && hasPermissions.action),
            label: i('task-log.actions.responder'),
            icon: <ICONS.SettingIcon />,
            CustomComponent: (trigger) => (
                <ResponderDrawer
                    key={`${_id}-tasklog-responders`}
                    objectId={_id}
                    objectType="case_task_log"
                    triggerElem={trigger}
                />
            ),
        },
        count: {
            hidden: !actionCount,
            label: i(`task-log.responders.${showReports ? 'hide' : 'show'}`),
            icon: <ICONS.TableOutlined />,
            query: toggleReports,
        },
    };

    return (
        <>
            <Comment
                actions={actions}
                attachments={attachments}
                avatar={avatar}
                content={<THMarkdown value={message} disabled borderless onImageUpload={onCaseImageUpload(caseId)} />}
                date={_createdAt}
                edited={includeInTimeline}
                editedPrefix={i('task-log.field.includeInTimeline')}
                name={name}
                onTogglePreview={togglePreview}
                parentId={_id}
            />

            {showReports && <TaskLogItemReports taskLogId={_id} key="tasklog-report" className="task-log__report" />}
        </>
    );
};

export default TaskLogItem;
